import { LoadingOutlined } from "@ant-design/icons";
import { notification, theme } from "antd";
import { NotificationInstance } from "antd/es/notification/interface";
import { NotificationPlacement } from "antd/lib/notification/interface";
import React, { FC, createContext, useContext, useState } from "react";

const NotificationContext = createContext<NotificationInstance | null>(null);

export const NotificationProvider: FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [api, contextHolder] = notification.useNotification({
        stack: true,
        placement: "topRight",
        showProgress: true,
        pauseOnHover: true,
    });

    return (
        <NotificationContext.Provider value={api}>
            {contextHolder}
            {children}
        </NotificationContext.Provider>
    );
};

const CONSTANTS = {
    LOADING_DURATION: 10,
    DISMISS_DELAY: 100,
} as const;

const baseConfig = {
    placement: "topRight" as NotificationPlacement,
    duration: 3,
    showProgress: true,
    pauseOnHover: true,
};

type NotificationConfig = Partial<typeof baseConfig>;

interface BaseNotificationOptions {
    message: string | React.ReactNode;
    description?: string | React.ReactNode;
    config?: NotificationConfig;
}

interface DismissibleNotificationOptions extends BaseNotificationOptions {
    dismissKey?: string;
}

interface LoadingOptions {
    description?: string | React.ReactNode;
    config?: NotificationConfig;
}

type NotificationInput<T = BaseNotificationOptions> =
    | string
    | React.ReactNode
    | T;

export const useNotification = () => {
    const api = useContext(NotificationContext);
    const [loadingKey, setLoadingKey] = useState<string | null>(null);

    // Helper functions
    const generateKey = (type: string): string => `${type}-${Date.now()}`;

    const normalizeOptions = <T extends BaseNotificationOptions>(
        input: NotificationInput<T>,
        defaultMessage: string,
        additionalProps?: Partial<T>,
    ): T => {
        if (typeof input === "string" || React.isValidElement(input)) {
            return {
                message: input,
                ...(additionalProps ?? {}),
            } as T;
        }

        return {
            message: defaultMessage,
            ...(additionalProps ?? {}),
            ...(typeof input === "object" &&
            input !== null &&
            !React.isValidElement(input)
                ? input
                : {}),
        } as T;
    };

    const dismissNotification = (key: string): void => {
        if (!api) return;

        api.destroy(key);
        if (loadingKey === key) {
            setLoadingKey(null);
        }
    };

    const handleDismissibleNotification = (
        options: DismissibleNotificationOptions,
        notificationType: "success" | "error",
    ): void => {
        if (!api) return;

        // Dismiss loading notification if specified
        const keyToDismiss = options.dismissKey ?? loadingKey;
        if (keyToDismiss) {
            dismissNotification(keyToDismiss);
        }

        // Show notification after a brief delay
        setTimeout(() => {
            api[notificationType]({
                ...baseConfig,
                ...options.config,
                message: options.message,
                description: options.description,
            });
        }, CONSTANTS.DISMISS_DELAY);
    };

    // Public API
    const loading = (
        message: string | React.ReactNode,
        options: LoadingOptions = {},
    ): string | undefined => {
        if (!api) return;

        const key = generateKey("loading");
        setLoadingKey(key);

        api.open({
            ...baseConfig,
            ...options.config,
            message: message ?? "Loading...",
            description: options.description,
            icon: (
                <LoadingOutlined
                    style={{
                        color: theme.defaultConfig.token.colorPrimary,
                    }}
                />
            ),
            key,
            duration: CONSTANTS.LOADING_DURATION,
        });

        return key;
    };

    const dismiss = (key: string): void => {
        dismissNotification(key);
    };

    const info = (input: NotificationInput): void => {
        if (!api) return;

        const options = normalizeOptions(input, "Info");

        api.info({
            ...baseConfig,
            ...options.config,
            message: options.message,
            description: options.description,
        });
    };

    const success = (
        input: NotificationInput<DismissibleNotificationOptions>,
        dismissKey?: string,
    ): void => {
        const options = normalizeOptions(input, "Success!", { dismissKey });

        handleDismissibleNotification(options, "success");
    };

    const error = (
        input: NotificationInput<DismissibleNotificationOptions>,
        dismissKey?: string,
    ): void => {
        const options = normalizeOptions(input, "Something went wrong", {
            dismissKey,
        });

        handleDismissibleNotification(options, "error");
    };

    return { loading, success, error, info, dismiss };
};
